import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Slide} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { UserType } from "../../../../utils/constants";
import useAuth from "../../../../hooks/useAuth";
import userAxios from "../../../../utils/axios/v1/userAxios";
import { ReactComponent as HelpIcon } from "../../../../vendor/_new/icon/navbar/help.svg";
import { ReactComponent as EditIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { GreenSwitch } from "../../../common/Greens";
import { validateSuperUserSelection } from "../../../../utils/validation";
import { SaveButton, formSaveButtonSx } from "../../../common/Button";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";
import { SafeTextField } from "../../../common/Greens";

const sectionBoxSx = {
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: "8px",
  p: 4,
};

const sectionTitleSx = {
  fontSize: 18,
  fontWeight: "medium",
  color: colors.neutral[1000],
};

const descriptionSx = {
  fontSize: 14,
  color: colors.neutral[700],
};

const labelSx = {
  fontSize: 14,
  fontWeight: "medium",
  color: colors.neutral[1000],
};

const helperTextSx = {
  fontSize: 12,
  color: colors.neutral[600],
};

const saveBtnSx = {
  height: 36,
  borderRadius: "8px",
  backgroundColor: "#6371F7",
  border: "1px solid #6371F7",
  color: "#fff",
  fontSize: 14,
  textTransform: "none",
  "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
};

const resetBtnSx = {
  height: 36,
  borderRadius: "8px",
  borderColor: colors.neutral[300],
  color: colors.neutral[700],
  fontSize: 14,
  textTransform: "none",
  "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent", color: colors.neutral[900], fontWeight: 500 },
};

const kbLinkSx = {
  color: "#6371F7",
  fontSize: 14,
  textDecoration: "none",
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
};

const industryOptions = [
  { value: "finance", label: "Finance" },
  { value: "healthcare", label: "Healthcare" },
  { value: "retail", label: "Retail" },
  { value: "finance", label: "Financial Services" },
  { value: "technology", label: "Technology" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "education", label: "Education" },
  { value: "government", label: "Government" },
  { value: "other", label: "Other" },
];

const NewOrganisationSettingsPage = () => {
  const { userType } = useAuth();

  const [displayName, setDisplayName] = useState("sagepov");
  const [showTenantIndicator, setShowTenantIndicator] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState("software_b2b");
  const [openSuperUserDrawer, setOpenSuperUserDrawer] = useState(false);
  const [selectedSuperUserId, setSelectedSuperUserId] = useState(null);
  const [tempSelectedSuperUserId, setTempSelectedSuperUserId] = useState(null);
  const [superUser, setSuperUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [savingHeaderIndicator, setSavingHeaderIndicator] = useState(false);
  const [headerIndicatorMessage, setHeaderIndicatorMessage] = useState(null);
  const [headerIndicatorMessageType, setHeaderIndicatorMessageType] = useState(null);
  const [savingIndustry, setSavingIndustry] = useState(false);
  const [industryMessage, setIndustryMessage] = useState(null);
  const [industryMessageType, setIndustryMessageType] = useState(null);
  const [savingSuperUser, setSavingSuperUser] = useState(false);
  const [superUserMessage, setSuperUserMessage] = useState(null);
  const [superUserMessageType, setSuperUserMessageType] = useState(null);
  const [initialDisplayName, setInitialDisplayName] = useState("sagepov");
  const [initialShowTenantIndicator, setInitialShowTenantIndicator] = useState(false);
  const [initialIndustry, setInitialIndustry] = useState("software_b2b");
  const [baselineKey, setBaselineKey] = useState(0);

  const superUserSelectionValid = useMemo(
    () => validateSuperUserSelection(tempSelectedSuperUserId).valid,
    [tempSelectedSuperUserId],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsResponse, usersResponse, orgResponse] = await Promise.all([
          userAxios.get("/organisation/settings"),
          userAxios.get("/users"),
          userAxios.get("/organisation"),
        ]);
        
        const settings = settingsResponse.data;
        const orgData = orgResponse.data;
        
        if (settings.displayName !== undefined) {
          setDisplayName(settings.displayName);
          setInitialDisplayName(settings.displayName);
        }
        if (settings.showHeaderIndicator !== undefined) {
          setShowTenantIndicator(settings.showHeaderIndicator);
          setInitialShowTenantIndicator(settings.showHeaderIndicator);
        }
        if (settings.industryClassification !== undefined) {
          setSelectedIndustry(settings.industryClassification);
          setInitialIndustry(settings.industryClassification);
        }
        
        const users = (usersResponse.data || []).map(user => ({
          id: String(user.uid || user.id || user._id || ""),
          name: user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
          email: user.email || "",
          phone: user.phone || null,
        }));
        
        const uniqueUsers = users.filter((user, index, self) => 
          user.id && index === self.findIndex(u => u.id === user.id && u.email === user.email)
        );
        setAdminUsers(uniqueUsers);
        
        if (orgData.admin) {
          const adminName = orgData.admin.username || `${orgData.admin.firstName || ""} ${orgData.admin.lastName || ""}`.trim() || orgData.admin.email;
          
          const adminData = {
            id: orgData.admin.uid || orgData.admin.id || null,
            name: adminName,
            email: orgData.admin.email || "",
            phone: orgData.admin.phone || null,
          };
          
          setSuperUser(adminData);
          
          if (orgData.admin.email) {
            const matchingUser = uniqueUsers.find(u => u.email === orgData.admin.email);
            if (matchingUser) {
              setSelectedSuperUserId(matchingUser.id);
              setTempSelectedSuperUserId(matchingUser.id);
            } else if (adminData.id) {
              const adminIdStr = String(adminData.id);
              setSelectedSuperUserId(adminIdStr);
              setTempSelectedSuperUserId(adminIdStr);
            }
          } else if (adminData.id) {
            const adminIdStr = String(adminData.id);
            setSelectedSuperUserId(adminIdStr);
            setTempSelectedSuperUserId(adminIdStr);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  const superUserDetails = superUser ? {
    name: superUser.name || "",
    email: superUser.email || "",
    phone: superUser.phone || "N/A",
    role: "Organisation Admin",
    lastUpdated: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
  } : {
    name: "",
    email: "",
    phone: "N/A",
    role: "Organisation Admin",
    lastUpdated: "",
  };

  const maxDisplayNameLength = 25;

  useEffect(() => {
    if (openSuperUserDrawer && superUser && adminUsers.length > 0) {
      let userIdToSelect = tempSelectedSuperUserId || selectedSuperUserId;
      
      if (!userIdToSelect && superUser.email) {
        const matchingUser = adminUsers.find(u => u.email === superUser.email);
        if (matchingUser) {
          userIdToSelect = matchingUser.id;
        }
      }
      
      if (userIdToSelect) {
        const userIdStr = String(userIdToSelect);
        if (userIdStr !== tempSelectedSuperUserId) {
          setTempSelectedSuperUserId(userIdStr);
        }
      }
    }
  }, [openSuperUserDrawer, superUser, adminUsers, selectedSuperUserId, tempSelectedSuperUserId]);

  const handleCloseSuperUserDrawer = useCallback(() => {
    setOpenSuperUserDrawer(false);
    setSuperUserMessage(null);
    setSuperUserMessageType(null);
  }, []);

  const formSnapshot = useMemo(
    () => ({ tempSelectedSuperUserId }),
    [tempSelectedSuperUserId],
  );
  const isDirty = useDrawerBaselineDirty(openSuperUserDrawer, formSnapshot, baselineKey);
  const { requestClose: requestCloseSuperUserDrawer, confirmDialog: superUserConfirmDialog } = useGuardedDrawerClose(
    handleCloseSuperUserDrawer,
    isDirty,
    { disabled: savingSuperUser },
  );

  if (userType === UserType.ORG_USER) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 64px)",
          backgroundColor: "#fff",
        }}
      >
        <Stack
          alignItems="center"
          spacing={4}
          sx={{
            maxWidth: 400,
            textAlign: "center",
            p: 6,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#EEF0FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlined
              sx={{
                fontSize: 40,
                color: "#6371F7",
              }}
            />
          </Box>
          <Stack spacing={2}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 600,
                lineHeight: "36px",
                color: "#1A1A1A",
              }}
            >
              Permission Denied
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "21px",
                color: "#6B7280",
              }}
            >
              You don't have the required permissions to access Organisation Settings. Please contact your organisation administrator for access.
            </Typography>
          </Stack>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{
              height: 36,
              borderRadius: "8px",
              fontSize: 14,
              textTransform: "none",
              backgroundColor: "#6371F7",
              "&:hover": {
                backgroundColor: "#07f",
              },
            }}
          >
            Go Back
          </Button>
        </Stack>
      </Box>
    );
  }

  const handleHeaderIndicatorSave = async () => {
    try {
      setSavingHeaderIndicator(true);
      setHeaderIndicatorMessage(null);
      setHeaderIndicatorMessageType(null);
      
      const response = await userAxios.patch("/organisation/header-indicator", {
        displayName,
        showHeaderIndicator: showTenantIndicator,
      });
      
      if (response.data.displayName !== undefined) {
        setInitialDisplayName(response.data.displayName);
      }
      if (response.data.showHeaderIndicator !== undefined) {
        setInitialShowTenantIndicator(response.data.showHeaderIndicator);
      }
      
      setHeaderIndicatorMessage("Header indicator settings saved successfully");
      setHeaderIndicatorMessageType("success");
      
      window.dispatchEvent(new Event("organisationSettingsUpdated"));
      
      setTimeout(() => {
        setHeaderIndicatorMessage(null);
        setHeaderIndicatorMessageType(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving header indicator settings:", err);
      setHeaderIndicatorMessage(err.message || "Failed to save header indicator settings");
      setHeaderIndicatorMessageType("error");
    } finally {
      setSavingHeaderIndicator(false);
    }
  };

  const handleHeaderIndicatorReset = () => {
    setDisplayName(initialDisplayName);
    setShowTenantIndicator(initialShowTenantIndicator);
  };

  const handleIndustrySave = async () => {
    try {
      setSavingIndustry(true);
      setIndustryMessage(null);
      setIndustryMessageType(null);
      
      const response = await userAxios.patch("/organisation/industry", {
        industryClassification: selectedIndustry,
      });
      
      if (response.data.industryClassification !== undefined) {
        setInitialIndustry(response.data.industryClassification);
      }
      
      setIndustryMessage("Industry classification saved successfully");
      setIndustryMessageType("success");
      
      setTimeout(() => {
        setIndustryMessage(null);
        setIndustryMessageType(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving industry classification:", err);
      setIndustryMessage(err.message || "Failed to save industry classification");
      setIndustryMessageType("error");
    } finally {
      setSavingIndustry(false);
    }
  };

  const handleIndustryReset = () => {
    setSelectedIndustry(initialIndustry);
  };

  const handleOpenSuperUserDrawer = () => {
    let userIdToSelect = selectedSuperUserId;
    
    if (superUser && superUser.email && adminUsers.length > 0) {
      const matchingUser = adminUsers.find(u => {
        const emailMatch = u.email === superUser.email;
        const idMatch = u.id === selectedSuperUserId || u.id === superUser.id;
        return emailMatch || idMatch;
      });
      
      if (matchingUser) {
        userIdToSelect = matchingUser.id;
      }
    }
    
    if (userIdToSelect) {
      setTempSelectedSuperUserId(String(userIdToSelect));
    } else if (adminUsers.length > 0 && selectedSuperUserId) {
      const userExists = adminUsers.find(u => u.id === selectedSuperUserId);
      if (userExists) {
        setTempSelectedSuperUserId(String(selectedSuperUserId));
      }
    }
    
    setOpenSuperUserDrawer(true);
    setBaselineKey((k) => k + 1);
  };

  const handleSaveSuperUser = async () => {
    const selectionCheck = validateSuperUserSelection(tempSelectedSuperUserId);
    if (!selectionCheck.valid) {
      setSuperUserMessage(selectionCheck.message);
      setSuperUserMessageType("error");
      return;
    }

    try {
      setSavingSuperUser(true);
      setSuperUserMessage(null);
      setSuperUserMessageType(null);
      
      const selectedUser = adminUsers.find(u => u.id === tempSelectedSuperUserId);
      if (!selectedUser) {
        throw new Error("Selected user not found");
      }

      const response = await userAxios.patch("/organisation/administrator", {
        userId: tempSelectedSuperUserId,
      });
      
      if (response.data.admin) {
        const adminData = {
          id: String(response.data.admin.uid || response.data.admin.id || tempSelectedSuperUserId),
          name: response.data.admin.username || `${response.data.admin.firstName || ""} ${response.data.admin.lastName || ""}`.trim() || response.data.admin.email,
          email: response.data.admin.email || "",
          phone: response.data.admin.phone || null,
        };
        setSuperUser(adminData);
        setSelectedSuperUserId(String(tempSelectedSuperUserId));
      } else {
        setSuperUser(selectedUser);
        setSelectedSuperUserId(String(tempSelectedSuperUserId));
      }
      
      setSuperUserMessage("Super user updated successfully");
      setSuperUserMessageType("success");
      
      setTimeout(() => {
        setSuperUserMessage(null);
        setSuperUserMessageType(null);
    setOpenSuperUserDrawer(false);
      }, 2000);
    } catch (err) {
      console.error("Error saving super user:", err);
      setSuperUserMessage(err.message || "Failed to update super user");
      setSuperUserMessageType("error");
    } finally {
      setSavingSuperUser(false);
    }
  };

  return (
    <>
      <Helmet title="Organisation Settings" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <V2PageHeader
            title="Organisation Settings"
            description="Configure your organisation's display settings, industry classification, and manage super user details."
            sx={{ pb: 0 }}
          />

          <Box sx={sectionBoxSx}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography sx={sectionTitleSx}>Header Indicator</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="https://docs.layerdai.com/settings/organisation/header-indicator"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={kbLinkSx}
                >
                  Header Indicator
                </Typography>
              </Stack>
            </Stack>

            {headerIndicatorMessage && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: headerIndicatorMessageType === "success" ? "#D1FAE5" : "#FEE2E2",
                  border: `1px solid ${headerIndicatorMessageType === "success" ? "#10B981" : "#EF4444"}`,
                }}
              >
                <Typography sx={ { fontSize: 14, color: headerIndicatorMessageType === "success" ? "#065F46" : "#B91C1C" }}>
                  {headerIndicatorMessage}
                </Typography>
              </Stack>
            )}

            <Stack spacing={4}>
              <Box>
                <Typography sx={{ ...labelSx, mb: 1 }}>Display Name</Typography>
                <Typography sx={{ ...descriptionSx, mb: 2, lineHeight: 1.6 }}>
                  A label that will be used to identify your account. It will be visible in the profile menu and optionally in the header indicator, if enabled.
                </Typography>
                <SafeTextField
                  value={displayName}
                  onChange={(e) => {
                    if (e.target.value.length <= maxDisplayNameLength) {
                      setDisplayName(e.target.value);
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  sx={{
                    maxWidth: 400,
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      borderRadius: "8px",
                      fontSize: 14,
                      "& fieldset": {
                        borderColor: colors.neutral[300],
                      },
                      "&:hover fieldset": {
                        borderColor: colors.neutral[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.primary[300],
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      fontSize: 14,
                      lineHeight: "21px",
                    },
                  }}
                />
                <Typography sx={{ ...helperTextSx, mt: 1 }}>
                  {displayName.length}/{maxDisplayNameLength} characters max
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ ...labelSx, mb: 1 }}>Tenant Indicator Badge</Typography>
                <Typography sx={{ ...descriptionSx, mb: 2, lineHeight: 1.6 }}>
                  The tenant indicator is a badge that appears for all users at the top left edge of the screen and includes the organisation display name and color. This helps users who manage multiple organisations to easily identify which one they are currently logged into.
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <GreenSwitch
                    checked={showTenantIndicator}
                    onChange={(e) => setShowTenantIndicator(e.target.checked)}
                  />
                  <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>
                    Show Tenant Indicator
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                variant="contained" 
                sx={saveBtnSx} 
                onClick={handleHeaderIndicatorSave}
                disabled={savingHeaderIndicator}
              >
                {savingHeaderIndicator ? "Saving..." : "Save"}
              </Button>
              <Button 
                variant="outlined" 
                sx={resetBtnSx} 
                onClick={handleHeaderIndicatorReset}
                disabled={savingHeaderIndicator}
              >
                Reset
              </Button>
            </Stack>
          </Box>

          <Box sx={sectionBoxSx}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography sx={sectionTitleSx}>Industry Classification</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="https://docs.layerdai.com/settings/organisation/industry"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={kbLinkSx}
                >
                  Industry Classification
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ ...descriptionSx, mb: 4, lineHeight: 1.6 }}>
              Layerd uses this to identify your industry. This selection is used for calculating security benchmarks specific to your sector, unless you have disabled the security score in your dashboard settings.
            </Typography>

            {industryMessage && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: industryMessageType === "success" ? "#D1FAE5" : "#FEE2E2",
                  border: `1px solid ${industryMessageType === "success" ? "#10B981" : "#EF4444"}`,
                  mb: 4,
                }}
              >
                <Typography sx={ { fontSize: 14, color: industryMessageType === "success" ? "#065F46" : "#B91C1C" }}>
                  {industryMessage}
                </Typography>
              </Stack>
            )}

            <Box>
              <Typography sx={{ ...labelSx, mb: 1.5 }}>Select Industry</Typography>
              <FormControl>
                <Select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  sx={{
                    height: 40,
                    minWidth: 200,
                    borderRadius: "8px",
                    fontSize: 14,
                    "& .MuiSelect-select": {
                      fontSize: 14,
                      lineHeight: "21px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.neutral[300],
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.neutral[400],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.primary[300],
                    },
                  }}
                >
                  {industryOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={ { fontSize: 14 }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                variant="contained" 
                sx={saveBtnSx} 
                onClick={handleIndustrySave}
                disabled={savingIndustry}
              >
                {savingIndustry ? "Saving..." : "Save"}
              </Button>
              <Button 
                variant="outlined" 
                sx={resetBtnSx} 
                onClick={handleIndustryReset}
                disabled={savingIndustry}
              >
                Reset
              </Button>
            </Stack>
          </Box>

          <Box sx={sectionBoxSx}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={sectionTitleSx}>Organisation Super User</Typography>
                <Typography
                  component="span"
                  onClick={handleOpenSuperUserDrawer}
                  sx={{
                    color: "#6371F7",
                    fontSize: 14,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <EditIcon style={{ width: 16, height: 16, color: "#6371F7" }} />
                  Edit
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="https://docs.layerdai.com/settings/organisation/super-user"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={kbLinkSx}
                >
                  Organisation Super User
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ ...descriptionSx, mb: 4, lineHeight: 1.6 }}>
              The super user is the primary administrator for this organisation with full access to all settings and features.
            </Typography>

            <Stack spacing={3.5}>
              <Stack direction="row" spacing={6}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={helperTextSx}>Name</Typography>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                    {superUserDetails.name}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={helperTextSx}>Role</Typography>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: colors.primary[100],
                      borderRadius: "4px",
                      px: 2,
                      py: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Typography sx={ { fontSize: 12, color: colors.primary[400] }}>
                      {superUserDetails.role}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              <Stack direction="row" spacing={6}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={helperTextSx}>Email</Typography>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                    {superUserDetails.email}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={helperTextSx}>Phone</Typography>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                    {superUserDetails.phone || "N/A"}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography sx={helperTextSx}>Last Updated</Typography>
                <Typography sx={ { fontSize: 14, color: colors.neutral[700], mt: 0.5 }}>
                  {superUserDetails.lastUpdated}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Modal open={openSuperUserDrawer} onClose={requestCloseSuperUserDrawer}>
        <Slide direction="left" in={openSuperUserDrawer} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EditIcon style={{ width: 28, height: 28, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                    Change Super User
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Select a new primary administrator for your organisation.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={requestCloseSuperUserDrawer} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>

            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent="space-between" spacing={4} sx={{ pr: 4, height: "100%" }}>
                  <Stack spacing={4}>
                    {superUserMessage && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: superUserMessageType === "success" ? "#D1FAE5" : "#FEE2E2",
                          border: `1px solid ${superUserMessageType === "success" ? "#10B981" : "#EF4444"}`,
                        }}
                      >
                        <Typography sx={ { fontSize: 14, color: superUserMessageType === "success" ? "#065F46" : "#B91C1C" }}>
                          {superUserMessage}
                        </Typography>
                      </Stack>
                    )}
                    <Box>
                      <Typography sx={{ ...labelSx, mb: 1.5 }}>Select Administrator</Typography>
                      <FormControl fullWidth>
                        <Select
                          value={tempSelectedSuperUserId || ""}
                          onChange={(e) => setTempSelectedSuperUserId(String(e.target.value))}
                          renderValue={(value) => {
                            if (!value) return "";
                            const selectedUser = adminUsers.find(u => u.id === value);
                            if (selectedUser) {
                              // const showName = selectedUser.name && selectedUser.name !== selectedUser.email;
                              return (
                                <Stack>
                                  {/* {showName && (
                                    <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                                      {selectedUser.name}
                                    </Typography>
                                  )} */}
                                  <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                                    {selectedUser.email}
                                  </Typography>
                                </Stack>
                              );
                            }
                            return "";
                          }}
                          sx={{
                            height: 48,
                            borderRadius: "8px",
                            fontSize: 14,
                            "& .MuiSelect-select": {
                              fontSize: 14,
                              lineHeight: "21px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.neutral[300],
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.neutral[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.primary[300],
                            },
                          }}
                        >
                          {adminUsers.length > 0 ? (
                            adminUsers.map((user) => {
                              const showName = user.name && user.name !== user.email;
                              return (
                                <MenuItem
                                  key={user.id}
                                  value={user.id}
                                  sx={ { fontSize: 14, py: 1.5, "&:hover": { color: colors.primary[300] } }}
                                >
                                  <Stack>
                                    {showName && (
                                      <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                                        {user.name}
                                      </Typography>
                                    )}
                                    <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
                                      {user.email}
                                    </Typography>
                                  </Stack>
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem disabled>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                                No users available
                              </Typography>
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    {tempSelectedSuperUserId && (
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: colors.neutral[50],
                          border: `1px solid ${colors.neutral[200]}`,
                          borderRadius: "8px",
                        }}
                      >
                        <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 2 }}>
                          Selected Administrator Details
                        </Typography>
                        {(() => {
                          const selectedUser = adminUsers.find(u => u.id === tempSelectedSuperUserId);
                          if (!selectedUser) return null;
                          return (
                            <Stack spacing={2}>
                              <Box>
                                <Typography sx={helperTextSx}>Name</Typography>
                                <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                                  {selectedUser.name}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={helperTextSx}>Email</Typography>
                                <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                                  {selectedUser.email}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={helperTextSx}>Phone</Typography>
                                <Typography sx={ { fontSize: 14, color: colors.neutral[1000], mt: 0.5 }}>
                                  {selectedUser.phone || "N/A"}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })()}
                      </Box>
                    )}

                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        minHeight: 48,
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: colors.primary["alpha"],
                        border: `solid 1px ${colors.primary[300]}`,
                      }}
                    >
                      <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
                        <HelpIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                      </Box>
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                        Changes will be applied instantly after save. The new super user will receive full administrative access.
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={3}>
                    <SaveButton
                      onClick={handleSaveSuperUser}
                      disabled={savingSuperUser || !superUserSelectionValid}
                      loading={savingSuperUser}
                      label={savingSuperUser ? "Saving..." : "Save"}
                      sx={formSaveButtonSx}
                    />
                    <Button
                      onClick={requestCloseSuperUserDrawer}
                      disabled={savingSuperUser}
                      sx={{
                        height: 44,
                        width: "50%",
                        borderRadius: "8px",
                        border: `1px solid ${colors.neutral[300]}`,
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.neutral[700],
                        textTransform: "none",
                        fontWeight: "regular",
                        "&:hover": { backgroundColor: colors.neutral[50] },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>
      {superUserConfirmDialog}
    </>
  );
};

export default NewOrganisationSettingsPage;
